import React, { useState } from 'react';
import { Folder as FolderIcon, ChevronDown, ChevronRight, Plus, MoreVertical } from 'lucide-react';
import CardStack from './CardStack';

const Folder = ({ 
  folder, 
  groups, 
  cards, 
  onGroupClick, 
  onShareClick, 
  currentUser,
  onAddGroup,
  onRenameFolder,
  onDeleteFolder,
  groupSortOrder
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showOptions, setShowOptions] = useState(false);

  // Filter groups that belong to this folder
  let folderGroups = groups.filter(group => {
    // Ensure group is an object with id and name
    if (!group || typeof group !== 'object') return false;
    return group.folderId === folder.id;
  });

  // Sort folderGroups based on groupSortOrder
  if (groupSortOrder === 'alpha') {
    folderGroups = [...folderGroups].sort((a, b) => a.name.localeCompare(b.name));
  } else if (groupSortOrder === 'recent') {
    // Sort by most recent card in each group (descending)
    const getGroupLastModified = (groupName) => {
      const groupCards = cards.filter(card => {
        if (card.deleted) return false;
        const cardGroups = new Set();
        if (card.group) cardGroups.add(card.group);
        if (Array.isArray(card.groups)) {
          card.groups.forEach(g => cardGroups.add(g));
        }
        if (card.isShared && card.sharedGroups) {
          card.sharedGroups.forEach(g => cardGroups.add(g));
        }
        return cardGroups.has(groupName);
      });
      return Math.max(...groupCards.map(card => {
        const updatedAt = card.updatedAt && typeof card.updatedAt.toMillis === 'function' ? card.updatedAt.toMillis() : (card.updatedAt instanceof Date ? card.updatedAt.getTime() : (typeof card.updatedAt === 'number' ? card.updatedAt : 0));
        const createdAt = card.createdAt && typeof card.createdAt.toMillis === 'function' ? card.createdAt.toMillis() : (card.createdAt instanceof Date ? card.createdAt.getTime() : (typeof card.createdAt === 'number' ? card.createdAt : 0));
        return Math.max(updatedAt, createdAt);
      }), 0);
    };
    folderGroups = [...folderGroups].sort((a, b) => getGroupLastModified(b.name) - getGroupLastModified(a.name));
  }

  // Filter cards that belong to this folder
  const folderCards = cards.filter(card => card.folderId === folder.id && !card.deleted);

  // Get ungrouped cards (cards without a group)
  const ungroupedCards = folderCards.filter(card => !card.group && (!card.groups || card.groups.length === 0));

  // Get cards that belong to groups
  const groupedCards = folderCards.filter(card => card.group || (card.groups && card.groups.length > 0));

  // Create a map of group IDs to their display names
  const groupNameMap = new Map();
  folderGroups.forEach(group => {
    if (group && typeof group === 'object') {
      groupNameMap.set(group.id, group.name);
    }
  });

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
      {/* Folder Header */}
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
          <FolderIcon className="w-5 h-5 text-[#04b53a]" />
          <h3 className="text-lg font-semibold text-gray-900">{folder.name}</h3>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddGroup(folder.id);
            }}
            className="p-1 text-gray-600 hover:text-[#04b53a] hover:bg-gray-50 rounded-full transition-colors"
            title="Add group to folder"
          >
            <Plus className="w-5 h-5" />
          </button>
          
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowOptions(!showOptions);
              }}
              className="p-1 text-gray-600 hover:text-[#04b53a] hover:bg-gray-50 rounded-full transition-colors"
              title="Folder options"
            >
              <MoreVertical className="w-5 h-5" />
            </button>
            
            {showOptions && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRenameFolder(folder);
                    setShowOptions(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Rename Folder
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteFolder(folder);
                    setShowOptions(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50"
                >
                  Delete Folder
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Folder Content */}
      {isExpanded && (
        <div className="px-4 pb-4">
          {folderGroups.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {folderGroups.map(group => (
                <CardStack
                  key={group.id}
                  group={group}
                  cards={cards}
                  onCardClick={onGroupClick}
                  onShareClick={onShareClick}
                  currentUser={currentUser}
                  onMoveGroup={() => {}}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No groups in this folder yet
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Folder; 